import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Group, Panel, Separator, useDefaultLayout, type Layout } from 'react-resizable-panels';

import {
  activatePanel,
  dockPanel,
  externalizePanel,
  floatPanel,
  moveFloat,
  moveTab,
  openPanels,
  removePanel,
  restorePanel,
  setSizes,
  toggleMaximize,
  visiblePanels,
  type DockNode,
  type DockState,
  type DropZone,
  type FloatWindow,
  type TabsNode,
} from '../../lib/dock-model.js';
import { installShortcutDispatcher } from '../../lib/shortcuts/index.js';
import { ContextMenu, type MenuItem } from './controls.js';
import { Icon, type IconName } from './icons.js';

/**
 * Resizable split that remembers its layout in localStorage.
 * Sizes in `fallback` are percentages; Panel min sizes are pixels.
 */
export function SplitGroup({
  id,
  orientation,
  fallback,
  panelIds,
  children,
  className = '',
}: {
  id: string;
  orientation: 'horizontal' | 'vertical';
  fallback: Layout;
  panelIds?: string[];
  children: ReactNode;
  className?: string;
}) {
  const saved = useDefaultLayout({
    id: `ai-studio:${id}`,
    storage: typeof localStorage === 'undefined' ? undefined : localStorage,
    ...(panelIds ? { panelIds } : {}),
  });
  const layout = saved.defaultLayout && Object.keys(fallback).every((k) => k in saved.defaultLayout!)
    ? saved.defaultLayout
    : fallback;
  return (
    <Group
      orientation={orientation}
      defaultLayout={layout}
      onLayoutChanged={saved.onLayoutChanged}
      className={`split ${className}`}
    >
      {children}
    </Group>
  );
}

/** Draggable divider; a thin line with a wide invisible hit area. */
export function Handle({ orientation }: { orientation: 'horizontal' | 'vertical' }) {
  return <Separator className={`handle handle-${orientation === 'vertical' ? 'v' : 'h'}`} />;
}

/* ======================================================================== */
/* Docking                                                                   */
/* ======================================================================== */

export interface DockPanelDef {
  id: string;
  title: string;
  icon?: IconName;
  render: () => ReactNode;
  /** Shows a close button (default true). */
  closable?: boolean;
  /** May open in its own OS window (no engine viewports: their GPU surface stays in the main window). */
  tearOff?: boolean;
  /** A document view: may sit in the primary group, whose tab strip is the top bar. */
  document?: boolean;
  /** Unmount while not on screen instead of staying mounted once shown. */
  unmountHidden?: boolean;
  /** Mounted from the start and never unmounted, even when closed or hidden (warm engine views). */
  alwaysMounted?: boolean;
  /** Smallest size in pixels along a split. */
  minSize?: number;
}

/** MIME type of a panel being dragged; the payload is the panel id. */
export const PANEL_DRAG_TYPE = 'application/x-ai-studio-panel';

/* Drag state is module-level so tabs outside the layout (the top bar) can start drags. */
let dragging: string | null = null;
const dragListeners = new Set<() => void>();
function setDragging(panel: string | null) {
  dragging = panel;
  dragListeners.forEach((l) => l());
}
function useDragging(): string | null {
  return useSyncExternalStore(
    (fn) => (dragListeners.add(fn), () => void dragListeners.delete(fn)),
    () => dragging,
  );
}

/** Make any element a drag source for a dock panel (top bar tabs use this). */
export function panelDragProps(panel: string) {
  return {
    draggable: true,
    onDragStart: (event: ReactDragEvent) => {
      event.dataTransfer.setData(PANEL_DRAG_TYPE, panel);
      event.dataTransfer.setData('text/plain', panel);
      event.dataTransfer.effectAllowed = 'move';
      // Defer: changing the DOM during dragstart cancels the drag in Chromium.
      setTimeout(() => setDragging(panel), 0);
    },
    onDragEnd: () => setDragging(null),
  };
}

interface DockContext {
  state: DockState;
  defs: ReadonlyMap<string, DockPanelDef>;
  change: (next: DockState) => void;
  host: (panel: string) => HTMLDivElement;
  primaryHeader: boolean;
  emptyPrimary?: ReactNode;
  focus?: (panel: string) => void;
  rootRef: { current: HTMLDivElement | null };
}

/**
 * A docking area: splits, tab groups, floating groups and torn-off windows.
 *
 * The layout is controlled (`state` / `onChange`), so callers persist it,
 * apply presets and react to it. Each panel's content is rendered once, into
 * a host element that is moved between groups, so panels keep their React
 * state, scroll position and GPU canvases while the layout changes.
 */
export function DockLayout({
  state,
  onChange,
  panels,
  onFocusPanel,
  primaryHeader = false,
  emptyPrimary,
  className = '',
  windowName = 'ai-studio-panel',
}: {
  state: DockState;
  onChange: (next: DockState) => void;
  panels: readonly DockPanelDef[];
  onFocusPanel?: (panel: string) => void;
  /** Show the primary group's own tab strip (off when e.g. a top bar switches it). */
  primaryHeader?: boolean;
  emptyPrimary?: ReactNode;
  className?: string;
  windowName?: string;
}) {
  const defs = useMemo(() => new Map(panels.map((p) => [p.id, p])), [panels]);
  const hosts = useRef(new Map<string, HTMLDivElement>());
  const rootRef = useRef<HTMLDivElement | null>(null);
  const focusRef = useRef(onFocusPanel);
  focusRef.current = onFocusPanel;

  const host = (panel: string) => {
    let el = hosts.current.get(panel);
    if (!el) {
      el = document.createElement('div');
      el.className = 'dock-host';
      el.dataset.dockPanel = panel;
      // Clicking into a panel focuses it (and its shortcut context).
      el.addEventListener('pointerdown', () => focusRef.current?.(panel), true);
      el.addEventListener('focusin', () => focusRef.current?.(panel));
      hosts.current.set(panel, el);
    }
    return el;
  };

  // Panels mount the first time they are shown and stay mounted after.
  const visible = visiblePanels(state);
  const open = openPanels(state);
  const [mounted, setMounted] = useState<ReadonlySet<string>>(() => new Set(visible));
  useEffect(() => {
    setMounted((prev) => (visible.every((p) => prev.has(p)) ? prev : new Set([...prev, ...visible])));
  }, [visible.join('|')]);

  const ctx: DockContext = {
    state,
    defs,
    change: onChange,
    host,
    primaryHeader,
    emptyPrimary,
    focus: onFocusPanel,
    rootRef,
  };

  const maximized = state.maximized ? findTabs(state, state.maximized) : null;
  const drag = useDragging();

  return (
    <div className={`dock${drag ? ' is-dragging' : ''} ${className}`} ref={rootRef}>
      {maximized && !state.floating.some((f) => f.group.id === maximized.id) ? (
        <DockGroup ctx={ctx} group={maximized} />
      ) : (
        <DockTree ctx={ctx} node={state.root} />
      )}
      {state.floating.map((float) => (
        <FloatingGroup key={float.id} ctx={ctx} float={float} />
      ))}
      {state.external.map((panel) => (
        <ExternalWindow
          key={panel}
          name={`${windowName}-${panel}`}
          title={defs.get(panel)?.title ?? panel}
          host={host(panel)}
          onClosed={() => onChange(restorePanel(removePanel(state, panel), panel))}
        />
      ))}
      {panels.map((def) => {
        const show = def.alwaysMounted || (def.unmountHidden ? visible.includes(def.id) : mounted.has(def.id) || visible.includes(def.id));
        // Closed panels stay mounted (a closed AI dock keeps its conversation).
        if (!show || (def.unmountHidden && !def.alwaysMounted && !open.includes(def.id))) return null;
        return <Fragment key={def.id}>{createPortal(def.render(), host(def.id))}</Fragment>;
      })}
    </div>
  );
}

function findTabs(state: DockState, id: string): TabsNode | null {
  const walk = (node: DockNode): TabsNode | null =>
    node.type === 'tabs' ? (node.id === id ? node : null) : node.children.map(walk).find(Boolean) ?? null;
  return walk(state.root) ?? state.floating.find((f) => f.group.id === id)?.group ?? null;
}

function DockTree({ ctx, node }: { ctx: DockContext; node: DockNode }) {
  if (node.type === 'tabs') return <DockGroup ctx={ctx} group={node} />;
  const layout: Layout = Object.fromEntries(node.children.map((c, i) => [c.id, node.sizes[i] ?? 0]));
  // Re-key on the child set so a structural change re-applies the stored sizes.
  const key = `${node.id}:${node.children.map((c) => c.id).join(',')}`;
  return (
    <Group
      key={key}
      orientation={node.dir === 'row' ? 'horizontal' : 'vertical'}
      defaultLayout={layout}
      onLayoutChanged={(next) => {
        const sizes = node.children.map((c) => next[c.id] ?? 0);
        if (sizes.every((s, i) => Math.abs(s - (node.sizes[i] ?? 0)) < 0.05)) return;
        ctx.change(setSizes(ctx.state, node.id, sizes));
      }}
      className="split dock-split"
    >
      {node.children.map((child, i) => (
        <Fragment key={child.id}>
          {i > 0 && <Handle orientation={node.dir === 'row' ? 'vertical' : 'horizontal'} />}
          <Panel id={child.id} minSize={minSizeOf(ctx, child)} className="dock-cell">
            <DockTree ctx={ctx} node={child} />
          </Panel>
        </Fragment>
      ))}
    </Group>
  );
}

function minSizeOf(ctx: DockContext, node: DockNode): number {
  if (node.type === 'split') return 80;
  return Math.max(80, ...node.panels.map((p) => ctx.defs.get(p)?.minSize ?? 0));
}

function zoneAt(rect: DOMRect, x: number, y: number): DropZone {
  const fx = (x - rect.left) / rect.width;
  const fy = (y - rect.top) / rect.height;
  const edges: Array<[DropZone, number]> = [
    ['left', fx],
    ['right', 1 - fx],
    ['top', fy],
    ['bottom', 1 - fy],
  ];
  const [zone, distance] = edges.reduce((a, b) => (b[1] < a[1] ? b : a));
  return distance < 0.28 ? zone : 'center';
}

function DockGroup({ ctx, group, float }: { ctx: DockContext; group: TabsNode; float?: FloatWindow }) {
  const slot = useRef<HTMLDivElement | null>(null);
  const body = useRef<HTMLDivElement | null>(null);
  const drag = useDragging();
  const [zone, setZone] = useState<DropZone | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; panel: string } | null>(null);
  const active = group.active;
  const headerless = group.primary && !ctx.primaryHeader && !float;
  const maximized = ctx.state.maximized === group.id;

  // Show the active panel's host in this group's slot (hosts are stable per panel).
  useLayoutEffect(() => {
    const el = slot.current;
    if (!el || !active) return;
    const h = ctx.host(active);
    if (h.parentNode !== el) {
      el.replaceChildren(h);
      // Views that size themselves from the window re-measure.
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    }
    return () => {
      if (h.parentNode === el) el.removeChild(h);
    };
  }, [active]);

  const accepts = (panel: string | null, z: DropZone): boolean => {
    if (!panel) return false;
    const def = ctx.defs.get(panel);
    if (float) return z === 'center';
    // The primary group's tabs are the top bar's: documents only.
    if (group.primary && z === 'center' && !def?.document) return false;
    if (group.panels.length === 1 && group.panels[0] === panel) return false;
    return true;
  };

  const over = (event: ReactDragEvent, forced?: DropZone) => {
    const panel = drag ?? (event.dataTransfer.types.includes(PANEL_DRAG_TYPE) ? '?' : null);
    if (!panel) return;
    const rect = body.current?.getBoundingClientRect();
    let z: DropZone = forced ?? (rect ? zoneAt(rect, event.clientX, event.clientY) : 'center');
    if (!accepts(drag, z) && z === 'center' && rect) {
      // Fall back to the nearest edge (the primary group refuses tool tabs).
      const fx = (event.clientX - rect.left) / rect.width;
      z = fx < 0.5 ? 'left' : 'right';
    }
    if (drag && !accepts(drag, z)) {
      setZone(null);
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (z !== zone) setZone(z);
  };

  const drop = (event: ReactDragEvent, index?: number) => {
    const panel = event.dataTransfer.getData(PANEL_DRAG_TYPE) || drag;
    const z = zone;
    setZone(null);
    setDragging(null);
    if (!panel || !z) return;
    event.preventDefault();
    event.stopPropagation();
    let next = dockPanel(ctx.state, panel, group.id, z, z === 'center' ? undefined : 30);
    if (z === 'center' && index !== undefined) next = moveTab(next, panel, index);
    ctx.change(next);
    ctx.focus?.(panel);
  };

  const menuItems = (panel: string): MenuItem[] => {
    const def = ctx.defs.get(panel);
    const rect = ctx.rootRef.current?.getBoundingClientRect();
    const items: MenuItem[] = [
      { id: 'right', label: 'Move to Right Split', icon: 'panelRight', run: () => ctx.change(dockPanel(ctx.state, panel, group.id, 'right', 35)) },
      { id: 'down', label: 'Move to Bottom Split', icon: 'panelBottom', run: () => ctx.change(dockPanel(ctx.state, panel, group.id, 'bottom', 35)) },
      {
        id: 'float',
        label: 'Float',
        icon: 'copy',
        disabled: Boolean(float),
        run: () =>
          ctx.change(
            floatPanel(ctx.state, panel, {
              x: Math.round((rect?.width ?? 900) / 2 - 220),
              y: 60,
              w: 440,
              h: Math.round(Math.min(520, (rect?.height ?? 600) - 120)),
            }),
          ),
      },
    ];
    if (def?.tearOff) items.push({ id: 'window', label: 'Open in New Window', icon: 'fullscreen', run: () => ctx.change(externalizePanel(ctx.state, panel)) });
    if (!float) items.push({ id: 'max', label: maximized ? 'Restore Layout' : 'Maximize', icon: 'fullscreen', shortcut: 'Ctrl+Space', run: () => ctx.change(toggleMaximize(ctx.state, group.id)) });
    // Unity's "Add Tab": bring a closed panel into this group.
    const open = openPanels(ctx.state);
    const addable = [...ctx.defs.values()].filter((d) => !open.includes(d.id) && (!group.primary || float || d.document));
    if (addable.length) {
      items.push({ id: 'sep-add', label: '', separator: true }, { id: 'add-h', label: 'Add Tab', heading: true });
      for (const d of addable)
        items.push({
          id: `add:${d.id}`,
          label: d.title,
          icon: d.icon,
          run: () => {
            ctx.change(dockPanel(ctx.state, d.id, group.id, 'center'));
            ctx.focus?.(d.id);
          },
        });
    }
    if (def?.closable !== false) items.push({ id: 'sep', label: '', separator: true }, { id: 'close', label: 'Close Tab', icon: 'x', run: () => ctx.change(removePanel(ctx.state, panel)) });
    return items;
  };

  const showZones = drag !== null;

  return (
    <div
      className={`dock-group${group.primary ? ' is-primary' : ''}${float ? ' is-floating' : ''}${maximized ? ' is-maximized' : ''}`}
      data-dock-group={group.id}
    >
      {!headerless && (
        <div
          className="dock-tabs"
          role="tablist"
          onDragOver={(e) => over(e, 'center')}
          onDragLeave={() => setZone(null)}
          onDrop={(e) => drop(e, group.panels.length)}
          onPointerDown={float ? (e) => startMove(e, ctx, float) : undefined}
          onDoubleClick={() => !float && ctx.change(toggleMaximize(ctx.state, group.id))}
        >
          {group.panels.map((panel, index) => {
            const def = ctx.defs.get(panel);
            return (
              <button
                key={panel}
                type="button"
                role="tab"
                aria-selected={panel === active}
                className={`dock-tab${panel === active ? ' is-active' : ''}`}
                title={def?.title}
                {...panelDragProps(panel)}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => {
                  ctx.change(activatePanel(ctx.state, panel));
                  ctx.focus?.(panel);
                }}
                onAuxClick={(e) => {
                  if (e.button === 1 && def?.closable !== false) ctx.change(removePanel(ctx.state, panel));
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenu({ x: e.clientX, y: e.clientY, panel });
                }}
                onDragOver={(e) => over(e, 'center')}
                onDrop={(e) => drop(e, index)}
              >
                {def?.icon && <Icon name={def.icon} size={13} />}
                <span>{def?.title ?? panel}</span>
                {def?.closable !== false && (
                  <span
                    className="dock-tab-close"
                    role="button"
                    aria-label={`Close ${def?.title ?? panel}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      ctx.change(removePanel(ctx.state, panel));
                    }}
                  >
                    <Icon name="x" size={11} />
                  </span>
                )}
              </button>
            );
          })}
          <span className="dock-tabs-fill" />
          {active && (
            <button
              type="button"
              className="dock-tabs-more"
              title="Panel options"
              aria-label="Panel options"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setMenu({ x: r.right - 200, y: r.bottom + 2, panel: active });
              }}
            >
              <Icon name="more" size={14} />
            </button>
          )}
        </div>
      )}
      <div
        className="dock-body"
        ref={body}
        onDragOver={(e) => over(e)}
        onDragLeave={(e) => {
          if (!body.current?.contains(e.relatedTarget as Node | null)) setZone(null);
        }}
        onDrop={(e) => drop(e)}
      >
        <div className="dock-slot" ref={slot} />
        {!active && group.primary && <div className="dock-empty">{ctx.emptyPrimary ?? 'Drop a view here'}</div>}
        {showZones && <div className="dock-catch" />}
        {showZones && zone && <div className={`dock-zone is-${zone}`} />}
      </div>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menuItems(menu.panel)} onClose={() => setMenu(null)} />}
    </div>
  );
}

function startMove(event: ReactPointerEvent, ctx: DockContext, float: FloatWindow) {
  if (event.button !== 0) return;
  const startX = event.clientX;
  const startY = event.clientY;
  const root = ctx.rootRef.current?.getBoundingClientRect();
  const el = (event.currentTarget as HTMLElement).closest('.dock-float') as HTMLElement | null;
  let x = float.x;
  let y = float.y;
  const move = (e: PointerEvent) => {
    x = Math.max(0, Math.min(float.x + e.clientX - startX, (root?.width ?? 2000) - 80));
    y = Math.max(0, Math.min(float.y + e.clientY - startY, (root?.height ?? 2000) - 30));
    if (el) el.style.transform = `translate(${x - float.x}px, ${y - float.y}px)`;
  };
  const up = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    if (el) el.style.transform = '';
    ctx.change(moveFloat(activatePanel(ctx.state, float.group.active ?? ''), float.id, { x, y }));
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
}

function FloatingGroup({ ctx, float }: { ctx: DockContext; float: FloatWindow }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const resize = (event: ReactPointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const sx = event.clientX;
    const sy = event.clientY;
    let w = float.w;
    let h = float.h;
    const move = (e: PointerEvent) => {
      w = Math.max(220, float.w + e.clientX - sx);
      h = Math.max(140, float.h + e.clientY - sy);
      if (ref.current) {
        ref.current.style.width = `${w}px`;
        ref.current.style.height = `${h}px`;
      }
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      ctx.change(moveFloat(ctx.state, float.id, { w, h }));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <div
      ref={ref}
      className="dock-float"
      style={{ left: float.x, top: float.y, width: float.w, height: float.h }}
      onPointerDown={() => {
        if (ctx.state.floating.at(-1)?.id !== float.id && float.group.active)
          ctx.change(activatePanel(ctx.state, float.group.active));
      }}
    >
      <DockGroup ctx={ctx} group={float.group} float={float} />
      <div className="dock-float-resize" onPointerDown={resize} aria-hidden="true" />
    </div>
  );
}

/**
 * A panel in an OS window of its own. The window shares this document's
 * JavaScript (same origin, opened by us), so the panel keeps running against
 * the same stores; its host element just moves into the other document.
 */
function ExternalWindow({ name, title, host, onClosed }: { name: string; title: string; host: HTMLDivElement; onClosed: () => void }) {
  const closed = useRef(onClosed);
  closed.current = onClosed;
  useEffect(() => {
    const win = window.open('', name, 'width=520,height=640');
    if (!win) {
      closed.current();
      return;
    }
    const doc = win.document;
    doc.title = title;
    doc.documentElement.className = document.documentElement.className;
    doc.head.replaceChildren(
      ...[...document.querySelectorAll('style, link[rel="stylesheet"]')].map((n) => n.cloneNode(true)),
    );
    doc.body.className = 'dock-external';
    doc.body.replaceChildren(host);
    const off = installShortcutDispatcher(win);
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      closed.current();
    };
    win.addEventListener('pagehide', finish);
    const poll = setInterval(() => win.closed && finish(), 500);
    const closeChild = () => win.close();
    window.addEventListener('pagehide', closeChild);
    return () => {
      done = true;
      clearInterval(poll);
      off();
      window.removeEventListener('pagehide', closeChild);
      if (host.ownerDocument === doc) host.remove();
      if (!win.closed) win.close();
    };
  }, [name, title, host]);
  return null;
}
